import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchPreviewForm, PreviewFormError } from '../api/AuthorizationApi';
import { useLanguage } from '../LanguageContext';
import { base64DecodeToJson } from '../utils/bas64Utils';
import { languageFromCode } from '../utils/languageMapping';
import QuestionnaireForm from './questionnaires/QuestionnaireForm';
import PreviewExpiredPage from './PreviewExpiredPage';
import { FormMetadata } from '../types';

interface PreviewFormLoaderProps {
  formData: FormMetadata | null;
  setFormData: (value: FormMetadata) => void;
  logout: () => void;
}

const PreviewFormLoader: React.FC<PreviewFormLoaderProps> = ({ formData, setFormData, logout }) => {
  const { linkId } = useParams<{ linkId: string }>();
  const { changeLanguage } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'expired' | 'error' | null>(null);

  useEffect(() => {
    if (!linkId) {
      setError('error');
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchPreviewForm(linkId);
        if (!cancelled) {
          const decoded = base64DecodeToJson(data.form_data);
          const resolvedLang = languageFromCode(data.language);
          changeLanguage(resolvedLang, undefined, false);
          setFormData(decoded);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          if (err instanceof PreviewFormError && (err.status === 404 || err.status === 410)) {
            setError('expired');
          } else {
            setError('error');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [linkId, setFormData, changeLanguage]);

  if (error) {
    return <PreviewExpiredPage />;
  }
  if (loading || !formData) {
    return (
      <div className='questionnaire-loading-overlay'>
        <div className='questionnaire-spinner'></div>
      </div>
    );
  }
  return (
    <QuestionnaireForm
      formData={formData}
      setFormData={setFormData}
      loadingForm={false}
      logout={logout}
      isPreview={true}
    />
  );
};

export default PreviewFormLoader;
